import React, { useCallback, useEffect, useMemo } from 'react'
import { Pagination, Spin, Tree } from 'antd'
import { get, isEmpty, map, values } from 'lodash'
import PropTypes from 'prop-types'

import { useQueryParams } from '../../../core/hooks'

import { TermErrors } from '../../../common/components/sections'
import { StyledVerticalSpace } from '../../../common/components/styled'
import { PAGINATION } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useLoggedInUser } from '../../../common/hooks'

import { DICTIONARY_WORD_TYPES } from '../../constants'
import {
  useCreateDictionaryWordsForTypeMutation,
  useDeleteDictionaryWordMutation,
  useDictionaryWordsQuery,
  useMoveDictionaryWordToTypeMutation,
  useUpdateDictionaryWordMutation,
} from '../../hooks'
import { getPaginationForDictionaryType } from '../../utils'

import DictionaryTypeNodeTitle from './dictionary-type-node-title'
import DictionaryWordNodeTitle from './dictionary-word-node-title'

const DictionaryWordTree = ({
  type,
  editable,
  languageOptions,
  languageOptionsGroupedByValue,
  refetchLastEvent,
}) => {
  const {
    settings: { pageSize: userPageSize },
  } = useLoggedInUser()

  const { setQueryParams, searchValue, languageIds, ...rest } = useQueryParams()

  const [page, pageSize] = useMemo(
    () => getPaginationForDictionaryType(rest, type, userPageSize),
    [rest, type, userPageSize],
  )

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    data: { results: dictionaryWords, total },
  } = useDictionaryWordsQuery(type, searchValue, languageIds, page, pageSize)

  const [
    updateDictionaryWord,
    { isLoading: isUpdateLoading, error: updateError },
  ] = useUpdateDictionaryWordMutation(type, refetchLastEvent)

  const [
    deleteDictionaryWord,
    { isLoading: isDeleteLoading, error: deleteError },
  ] = useDeleteDictionaryWordMutation(type, refetchLastEvent)

  const [
    createDictionaryWordsForType,
    { isLoading: isCreateLoading, error: createError },
  ] = useCreateDictionaryWordsForTypeMutation(type, refetchLastEvent)

  const [moveDictionaryWordToType] =
    useMoveDictionaryWordToTypeMutation(refetchLastEvent)

  const handleOnDelete = useCallback(
    id => {
      deleteDictionaryWord(
        id,
        { searchValue, languageIds },
        page,
        pageSize,
        refetchLastEvent,
      )
    },
    [
      deleteDictionaryWord,
      languageIds,
      page,
      pageSize,
      refetchLastEvent,
      searchValue,
    ],
  )

  const treeData = useMemo(
    () => [
      {
        id: '0',
        term: type.label,
        type: type.value,
        key: '0',
        children: map(dictionaryWords, dictionaryWord => ({
          ...dictionaryWord,
          key: dictionaryWord.id,
        })),
      },
    ],
    [dictionaryWords, type.label, type.value],
  )

  const handleChangePagination = useCallback(
    (newPage, newPageSize) =>
      setQueryParams({
        [type.shortName]: [newPageSize !== pageSize ? 1 : newPage, newPageSize],
      }),
    [setQueryParams, type.shortName, pageSize],
  )

  const handleOnRename = useCallback(
    (id, term) => updateDictionaryWord(id, { term }),
    [updateDictionaryWord],
  )

  const handleOnLanguageChange = useCallback(
    (id, languageId) => updateDictionaryWord(id, { languageId }),
    [updateDictionaryWord],
  )

  const handleOnMoveToType = useCallback(
    (id, type, previousType) => {
      const dictionaryTypeToMoveTo = get(DICTIONARY_WORD_TYPES, type)

      const [, typeToMoveToPageSize] = get(
        rest,
        dictionaryTypeToMoveTo.shortName,
        [PAGINATION.DEFAULT_PAGE, userPageSize || PAGINATION.DEFAULT_PAGE_SIZE],
      )

      moveDictionaryWordToType(
        id,
        type,
        typeToMoveToPageSize,
        previousType,
        pageSize,
        page,
        { searchValue, languageIds },
      )

      setQueryParams({
        [dictionaryTypeToMoveTo.shortName]: [
          PAGINATION.DEFAULT_PAGE,
          typeToMoveToPageSize,
        ],
      })
    },
    [
      languageIds,
      moveDictionaryWordToType,
      page,
      pageSize,
      rest,
      searchValue,
      setQueryParams,
      userPageSize,
    ],
  )

  const renderDictionaryWordNode = useCallback(
    node => {
      const isRootNode = node.key === '0'

      if (isRootNode) {
        return (
          <DictionaryTypeNodeTitle
            isCreateLoading={isCreateLoading}
            node={node}
            languageOptions={languageOptions}
            editable={editable}
            onCreate={createDictionaryWordsForType}
          />
        )
      }

      const language = get(
        languageOptionsGroupedByValue,
        `${node.languageId}[0]`,
      )

      return (
        <DictionaryWordNodeTitle
          searchValue={searchValue}
          node={node}
          editable={editable}
          onRename={handleOnRename}
          onDelete={handleOnDelete}
          onChangeLanguage={handleOnLanguageChange}
          onChangeType={handleOnMoveToType}
          refetchLastEvent={refetchLastEvent}
          language={language}
          languageOptions={languageOptions}
        />
      )
    },
    [
      languageOptionsGroupedByValue,
      searchValue,
      editable,
      handleOnRename,
      handleOnDelete,
      handleOnLanguageChange,
      handleOnMoveToType,
      refetchLastEvent,
      languageOptions,
      isCreateLoading,
      createDictionaryWordsForType,
    ],
  )

  useEffect(() => {
    if (fetchError || updateError || deleteError || createError) {
      showErrorNotification({
        title: 'Dictionary Word error',
        description:
          fetchError.message ||
          updateError.message ||
          deleteError.message ||
          createError.message ||
          fetchError,
      })
    }
  }, [fetchError, updateError, deleteError, createError])

  const { errorDetails } = updateError || createError || {}

  return isFetchLoading ||
    isUpdateLoading ||
    isDeleteLoading ||
    isCreateLoading ? (
    <Spin />
  ) : (
    <StyledVerticalSpace>
      {!isEmpty(errorDetails) && <TermErrors errors={errorDetails} />}
      <Tree
        defaultExpandAll
        titleRender={renderDictionaryWordNode}
        treeData={treeData}
      />
      <Pagination
        showSizeChanger
        onChange={handleChangePagination}
        onShowSizeChange={handleChangePagination}
        current={page}
        total={total}
        pageSize={pageSize}
        pageSizeOptions={PAGINATION.DEFAULT_PAGINATION_SIZE_OPTIONS}
      />
    </StyledVerticalSpace>
  )
}

DictionaryWordTree.propTypes = {
  type: PropTypes.oneOf(values(DICTIONARY_WORD_TYPES)).isRequired,
  editable: PropTypes.bool,
  languageOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  languageOptionsGroupedByValue: PropTypes.object.isRequired,
  refetchLastEvent: PropTypes.func.isRequired,
}

DictionaryWordTree.defaultProps = {
  editable: false,
}

export default DictionaryWordTree
